import { put, select } from "typed-redux-saga";
import { PlotData } from "plotly.js";
import { setStatus } from "../dom-utils";
import { rootActions } from "../reducers/root.slice";
import ColumnUtils from "../utils/ColumnUtils";
import GraphUtils from "../utils/GraphUtils";
import { selectRootState } from "../selectors";
import { generatePCACacheKey } from "../utils/utils";
import { workerActions } from "../reducers/worker.slice";
import { TRootState } from "../store/create-store";
import { selectPCAForCacheKey } from "../selectors/worker.selector";

function* generateGraphSaga(
  action: ReturnType<typeof rootActions.generateGraph>
) {
  try {
    const rootState = yield* select(selectRootState);
    const {
      leftPanel,
      rows,
      continuousColumns,
      continuousPredictionColumns,
      rightPanel,
      renderedState,
      histogramLayout,
      computedValues,
    } = rootState;

    const { debug } = yield* select((state) => state.rightPanel2);

    if (
      !leftPanel.selectedDataset ||
      !leftPanel.selectedMethod ||
      !leftPanel.selectedColumn
    ) {
      setStatus("No method or column selected");
      return;
    }

    setStatus(
      `Plotting method: ${leftPanel.selectedMethod} & column ${leftPanel.selectedColumn}...`
    );

    const PCACacheKey = generatePCACacheKey(
      leftPanel.selectedDataset,
      leftPanel.selectedMethod
    );
    const pcaForMethod = yield* select((state: TRootState) =>
      selectPCAForCacheKey(state, PCACacheKey)
    );

    let scatterPlotRows = rows;

    if (pcaForMethod) {
      if (rightPanel.projectToPCA && pcaForMethod.adjustedData) {
        const adjustedData = pcaForMethod.adjustedData;
        const x_key = leftPanel.selectedMethod;
        const { Y: y_key, Z: z_key } = ColumnUtils.getMethodColumnYZNames(
          leftPanel.selectedMethod
        );

        scatterPlotRows = scatterPlotRows.map((row, index) => {
          return {
            ...row,
            [x_key]: String(adjustedData[0][index]),
            [y_key]: String(adjustedData[1][index]),
            [z_key]: String(adjustedData[2][index]),
          };
        });
      }
    } else {
      yield* put(
        workerActions.calculatePCA({
          selectedDataset: leftPanel.selectedDataset,
          selectedMethod: leftPanel.selectedMethod,
        })
      );
    }

    const isConverted = Object.keys(
      rightPanel.categoricalColumnsAsContinuous
    ).includes(leftPanel.selectedColumn);

    const keepRange =
      renderedState.leftPanel.selectedDataset === leftPanel.selectedDataset &&
      renderedState.leftPanel.selectedColumn === leftPanel.selectedColumn &&
      renderedState.leftPanel.selectedMethod !== leftPanel.selectedMethod;

    const {
      graphData: newGraphData,
      sdMin,
      sdMax,
      farthestPointDistance,
    } = GraphUtils.generateGraphData({
      rows: scatterPlotRows,
      method: leftPanel.selectedMethod,
      column: leftPanel.selectedColumn,
      isContinuous: ColumnUtils.isContinuous(
        leftPanel.selectedColumn,
        continuousColumns,
        continuousPredictionColumns
      ),
      columnsForHover: leftPanel.columnsForHover.filter(
        (column) => column !== leftPanel.selectedColumn
      ),
      computedValues,
      range:
        action.payload?.range ?? keepRange
          ? (histogramLayout.xaxis?.range as [number, number])
          : undefined,
      sdFilter: rightPanel.sdFilter,
      showProjectionX: rightPanel.showProjectionX,
      showProjectionY: rightPanel.showProjectionY,
      showProjectionZ: rightPanel.showProjectionZ,
      rotation: debug.rotation,
      applyZScore: rightPanel.applyZScore,
      applyMinMaxScaling: rightPanel.applyMinMaxScaling,
    });

    let newGraphDataWithStyles = newGraphData.map((data) => ({
      ...data,
      marker: {
        ...data.marker,
        opacity: rightPanel.markerOpacity,
        size: rightPanel.markerSize,
        line: {
          ...data.marker.line,
          color: rightPanel.markerLineColor,
        },
      },
    }));

    if (pcaForMethod && rightPanel.showPCAAxis) {
      newGraphDataWithStyles = GraphUtils.addEigenVectors(
        newGraphDataWithStyles,
        pcaForMethod,
        farthestPointDistance
      ) as typeof newGraphDataWithStyles;
    }

    let newHistogramData: Partial<PlotData>[] = [];

    const isContinuous =
      !!leftPanel.selectedColumn &&
      ColumnUtils.isContinuous(
        leftPanel.selectedColumn,
        continuousColumns,
        continuousPredictionColumns
      );

    if (!isContinuous) {
      newHistogramData =
        GraphUtils.generateCategoricalHistogramData(newGraphData);
    } else {
      newHistogramData = GraphUtils.generateContinuousHistogramData({
        rows,
        selectedColumn: leftPanel.selectedColumn,
        sdMin,
        sdMax,
        isConverted,
      });

      yield* put(
        rootActions.setRightPanel({
          sdMin,
          sdMax,
        })
      );
    }

    // reset histogram range
    if (
      renderedState.leftPanel.selectedDataset !== leftPanel.selectedDataset ||
      renderedState.leftPanel.selectedColumn !== leftPanel.selectedColumn ||
      renderedState.rightPanel.sdFilter !== rightPanel.sdFilter ||
      renderedState.rightPanel.categoricalColumnsAsContinuous !==
        rightPanel.categoricalColumnsAsContinuous
    ) {
      yield* put(
        rootActions.setHistogramLayout({
          ...histogramLayout,
          barmode: isConverted ? "overlay" : "stack",
          dragmode: isContinuous ? "zoom" : false,
          xaxis: {
            ...histogramLayout.xaxis,
            autorange: true,
            range: undefined,
          },
          yaxis: {
            ...histogramLayout.yaxis,
            visible: false,
            autorange: true,
            range: undefined,
            type: isContinuous ? "linear" : "category",
          },
        })
      );

      yield* put(rootActions.setHistogramData(newHistogramData));
    }

    yield* put(rootActions.setScatterPlotData(newGraphDataWithStyles));
    yield* put(
      rootActions.setDensityPlotData(
        GraphUtils.generateDensityPlotData(newGraphDataWithStyles)
      )
    );
  } catch (error) {
    console.error(error);
    setStatus("Error generating graph: " + error.message);
  }
}

export default generateGraphSaga;
